import { useEffect } from 'react';
import {
  formatHdrAllowance,
  getActiveHdrPlans,
  hdrPlanPriceMinor,
  type HdrCurrency,
} from '@rehub/pricing';
import {
  HOME_FAQS,
  LANDING_CANONICAL,
  LANDING_DESCRIPTION,
  LANDING_H1,
} from '../lib/landing';

const SCRIPT_ID = 'hdr-landing-jsonld';

function money(minor: number): string {
  return (minor / 100).toFixed(2);
}

function planOffers() {
  const currencies: HdrCurrency[] = ['GBP', 'EUR', 'USD'];
  return getActiveHdrPlans().flatMap((plan) => {
    if (plan.isContact) {
      return [
        {
          '@type': 'Offer',
          name: plan.name,
          url: `${LANDING_CANONICAL}pricing`,
          availability: 'https://schema.org/InStock',
          description: 'Custom volume pricing for 5,000+ HDR Credits per month.',
        },
      ];
    }
    return currencies.map((currency) => ({
      '@type': 'Offer',
      name: `${plan.name} (${currency})`,
      url: `${LANDING_CANONICAL}pricing`,
      availability: 'https://schema.org/InStock',
      price: money(hdrPlanPriceMinor(plan, currency)),
      priceCurrency: currency,
      description: formatHdrAllowance(plan.imageAllowance),
    }));
  });
}

export default function LandingJsonLd() {
  useEffect(() => {
    const data = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'HDR Photo',
        applicationCategory: 'MultimediaApplication',
        operatingSystem: 'Web',
        url: LANDING_CANONICAL,
        description: LANDING_DESCRIPTION,
        image: `${LANDING_CANONICAL}og-default.webp`,
        offers: planOffers(),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: LANDING_H1,
        url: LANDING_CANONICAL,
        description: LANDING_DESCRIPTION,
        inLanguage: 'en-GB',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: HOME_FAQS.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
    return () => {
      script?.remove();
    };
  }, []);

  return null;
}
